import {
  faArrowRight,
  faCheckCircle,
  faExclamationCircle,
  faFilter,
  faPen,
  faRotateLeft,
} from "@fortawesome/free-solid-svg-icons";
import {
  GSListPage,
  GSEmptyList,
  defaultPadding,
  GSPageSection,
  defaultLayouts,
} from "golfstatus_react_components";
import { useEffect, useMemo } from "react";
import { useMatch } from "react-router-dom";
import { Outlet } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import { getTournamentPaymentSettings } from "../../../../reducers/tournamentSlice";
import {
  selectRegistrationOrder,
  getRegistrationOrderThunk,
  deleteOrderThunk,
  clearNotifications,
  saveCustomerThunk,
  updateOrderThunk,
} from "../../../../reducers/registrationOrderSlice";
import "./event-cart.scss";
import {
  useHeaderStyle,
  useSectionStyles,
  useTheme,
} from "../../../../hooks/themeHooks";
import CartStepItem from "./cart-step-item";
import {
  getOrderItemCount,
  getPrice,
  getRegistrationSummary,
  updatePlayerDetails,
  updateSponsorDetails,
  updateTeamContact,
  validateOrder,
} from "../../../../models/Registration";
import { getRouting } from "../../../../helpers/Converters";
import {
  ORDER_ID,
  TOURNAMENT_ID,
  eventBaseRoute,
  getProp,
} from "../../../../routes/event";
import PageHeader from "../../../page-header";
import { moneyWithCommas } from "../../../../helpers/RegexHelper";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { useNavigationButtons } from "../../../../hooks/pageHooks";
import PageFooter from "./page-footer";
import CartContactWidget from "../../../widgets/cart-contact-widget";
import CartMyPackagesWidget from "../../../widgets/cart-my-packages-widget";

import CartCustomResponses from "../../../widgets/cart-custom-responses";
import CartDonationWidget from "../../../widgets/cart-donation-widget";
import { usePermissions } from "../../../../hooks/permissionHooks";
import { getUserPaymentOptions } from "../../../../reducers/userSlice";

import {
  useCustomer,
  useLoading,
  usePaymentOptions,
  useTournament,
} from "../../../../hooks/tournamentHooks";
import { getCurrentUser } from "../../../../app/gs-session";
import {
  BANK_ACCOUNT,
  CREDIT_CARD,
  CUSTOMER_INVOICE,
  getActivePaymentMethods,
  PAY_AT_COURSE,
} from "../../../../forms/CartSections";
import { useNotificationBanner, useNotifications } from "../../../../hooks/notificationHooks";
import { useGolfstatusNavigate } from "../../../../hooks/navigationHooks";
import { selectLoggedInUserIsKnownNonAdmin } from "../../../../reducers/appSlice";

const EventCart = (props) => {
  const { style } = props;
  const navigate = useGolfstatusNavigate();
  const dispatch = useDispatch();

  const route = getRouting(eventBaseRoute, [
    TOURNAMENT_ID,
    "cart",
    ORDER_ID,
    "*",
  ]);
  const match = useMatch(route);

  const orderID = getProp(match?.params, ORDER_ID);
  const tournamentKey = getProp(match?.params, TOURNAMENT_ID)

  const tournament = useTournament();
  const publicUrl = tournament?.publicUrl;
  const tournamentID = tournament?.id;

  useEffect(() => {
    if (tournamentID) {
      dispatch(getTournamentPaymentSettings(tournamentID));
    }
  }, [dispatch, tournamentID]);

  useEffect(() => {
    if (orderID && tournamentID && orderID !== "new-order") {
      dispatch(getRegistrationOrderThunk(orderID));
    }
  }, [dispatch, orderID, tournamentID]);

  useEffect(() => {
    return () => {
      dispatch(clearNotifications());
    };
  }, [dispatch]);

  const [isLoading] = useLoading("getPublicTournamentDetails");

  const registrationOrder = useSelector(selectRegistrationOrder);
  const { customer, customerComplete } = useCustomer();
  const { lastCard } = usePaymentOptions();
  const canAutoApplySavedPayment = useSelector(selectLoggedInUserIsKnownNonAdmin);

  const [, theme, getStyle] = useTheme();
  const { defaultSectionStyle, headerSectionStyle } = useSectionStyles();

  const { headerStyle } = useHeaderStyle();

  const permissions = usePermissions();

  const getPageActions = useNavigationButtons("cart");

  useEffect(() => {
    const user = getCurrentUser();
    if (user?.uid) {
      dispatch(getUserPaymentOptions(user.uid));
    }
  }, [dispatch]);

  const getSearchActions = () => {
    return [
      {
        id: 1,
        type: "mobile-icon",
        actionIcon: faFilter,
      },
    ];
  };

  const getEmptyListMessage = () => {
    return (
      <GSEmptyList
        title="Your Cart Is Empty"
        detail=" First, you’ll need to add a package."
        actions={[
          {
            title: "View All Packages",
            type: "black",
            onClick: () => {
              navigate(getRouting(eventBaseRoute, [publicUrl, "packages"]));
            },
          },
        ]}
        style={{ ...defaultLayouts.fullWidth, ...defaultPadding.emptyListPad }}
      />
    );
  };

  const getEmptyListMessagePrivateEvent = () => {
    return (
      <GSEmptyList
        title="Your Cart Is Empty"
        detail={
          <center>
            {
              "This is a private event, please contact the organizer for access to the packages."
            }
          </center>
        }
        actions={[
          {
            title: "View Event Details",
            type: "black",
            onClick: () => {
              navigate(getRouting(eventBaseRoute, [publicUrl]));
            },
          },
        ]}
        style={{ ...defaultLayouts.fullWidth, ...defaultPadding.emptyListPad }}
      />
    );
  };

  const getEmptyListMessageDonationsActive = () => {
    return (
      <GSEmptyList
        title="Your Cart Is Empty"
        detail={
          <center>
            {
              "Registration has been closed for this event, but you can still donate."
            }
          </center>
        }
        actions={[
          {
            title: "Donate",
            type: "black",
            onClick: () => {
              navigate(getRouting(eventBaseRoute, [publicUrl, "donate"]));
            },
          },
        ]}
        style={{ ...defaultLayouts.fullWidth, ...defaultPadding.emptyListPad }}
      />
    );
  };

  const isSidePanelOpen = () => {
    return match?.params?.["*"] !== "";
  };

  const getRouter = () => {
    return <Outlet />;
  };

  const getNextStep = () => {
    if (!customerComplete()) {
      return {
        nextStep: "Add Contact Details",
        icon: (
          <FontAwesomeIcon
            style={{ ...theme.error.light }}
            icon={faExclamationCircle}
          />
        ),
        actions: [
          {
            title: "Add Details",
            buttonIcon: faPen,
            type: "orange",
            onClick: () => {
              navigate("customer");
            },
          },
          ...(getCurrentUser()?.uid
            ? []
            : [
                {
                  title: "sign in",
                  type: "grey",
                  onClick: () => {
                    window.location.href = process.env.REACT_APP_GOLFSTATUS_LOGIN_URL;
                  },
                },
              ]),
        ],
      };
    }

    const validations = validateOrder(registrationOrder, navigate);

    if (validations.length > 0) {
      return validations[0];
    }

    const itemCount = registrationOrder?.registrationOrderItems?.length;
    const donationCount = registrationOrder?.donationOrderItems?.length;

    if (itemCount + donationCount === 0) {
      return {
        nextStep: "Add Packages",
        icon: (
          <FontAwesomeIcon
            style={{ ...theme.error.light }}
            icon={faExclamationCircle}
          />
        ),
        actions: [
          {
            title: "Packages",
            rightIcon: faArrowRight,
            type: "grey",
            onClick: () => {
              navigate(getRouting(eventBaseRoute, [publicUrl, "packages"]));
            },
          },
        ],
      };
    }

    return {
      nextStep: "Next Step: Payment & Review",
      icon: (
        <FontAwesomeIcon
          style={{ ...getStyle(theme.tertiary) }}
          icon={faCheckCircle}
        />
      ),
      actions: [
        {
          title: "Continue",
          rightIcon: faArrowRight,
          type: "black",
          onClick: () => {
            continueToPayment();
          },
        },
      ],
    };
  };

  const continueToPayment = () => {
    const order = { ...registrationOrder };
    updateTeamContact(order, customer);
    updatePlayerDetails(order, customer);
    updateSponsorDetails(order, customer);

    const activePaymentMethods = getActivePaymentMethods(permissions)?.map?.(
      (apm) => apm.id
    );

    if (activePaymentMethods?.includes?.(1)) {
      order.paymentType = CREDIT_CARD;
    } else if (activePaymentMethods?.includes?.(2)) {
      order.paymentType = BANK_ACCOUNT;
    } else if (activePaymentMethods?.includes?.(4)) {
      order.paymentType = CUSTOMER_INVOICE;
    } else if (activePaymentMethods?.includes?.(3)) {
      order.paymentType = PAY_AT_COURSE;
    }

    if (canAutoApplySavedPayment && !order.paymentOption && order.paymentType === CREDIT_CARD) {
      order.paymentOption = lastCard;
    }

    dispatch(saveCustomerThunk({ ...customer, tournament }));
    dispatch(updateOrderThunk(order));

    navigate("checkout");
  };

  const cartActions = [
    {
      title: "restart order",
      buttonIcon: faRotateLeft,
      onClick: () => {
        dispatch(deleteOrderThunk(registrationOrder));
        navigate(getRouting(eventBaseRoute, [tournamentKey, "cart", "new-order"]))
      },
    },
  ];

  const showEmptyList = () => {
    if (
      !hasRegistrationOrderItems &&
      !hasDonation &&
      !permissions.publicRegistration
    ) {
      return getEmptyListMessagePrivateEvent();
    }

    if (
      !hasRegistrationOrderItems &&
      !hasDonation &&
      !permissions.registrationClosed
    ) {
      return getEmptyListMessage();
    }

    if (
      !hasRegistrationOrderItems &&
      !hasDonation &&
      permissions.donationsActive()
    ) {
      return getEmptyListMessageDonationsActive();
    }
  };

  const hasDonation = useMemo(() => {
    return (
      registrationOrder?.donationOrderItems?.filter?.((d) => !d.roundUp)
        ?.length > 0
    );
  }, [registrationOrder?.donationOrderItems]);

  const hasRegistrationOrderItems =
    registrationOrder?.registrationOrderItems?.length > 0;

  const getItemList = () => {
    const reviewItems = [<CartMyPackagesWidget />];

    if (registrationOrder?.customFieldResponses?.length > 0) {
      reviewItems.push(<CartCustomResponses />);
    }

    return (
      <div style={{ ...defaultLayouts.vStack }}>
        <PageHeader header="My Cart" actions={cartActions} />

        {hasRegistrationOrderItems || hasDonation ? (
          <GSPageSection
            {...headerSectionStyle}
            body={[<CartContactWidget />]}
          />
        ) : null}

        {showEmptyList()}

        {hasRegistrationOrderItems ? (
          <GSPageSection
            title="My Packages"
            bodyStyle={{ ...defaultPadding.noPad }}
            style={defaultSectionStyle?.style}
            body={reviewItems}
          />
        ) : null}

        {hasDonation ? (
          <GSPageSection
            title="My Donation"
            bodyStyle={{ ...defaultPadding.noPad }}
            style={defaultSectionStyle?.style}
            body={[<CartDonationWidget />]}
          />
        ) : null}

        <PageFooter showFooter={hasRegistrationOrderItems || hasDonation}>
          <CartStepItem
            itemCount={getOrderItemCount(registrationOrder)}
            {...getNextStep()}
            description={getRegistrationSummary(
              registrationOrder,
              moneyWithCommas(
                registrationOrder?.totalCost
                  ? registrationOrder?.totalCost
                  : getPrice(registrationOrder)
              )
            )}
            style={{
              ...defaultLayouts.vStack,
              ...defaultLayouts.fullWidth,
              maxWidth: "1080px",
            }}
          />
        </PageFooter>
      </div>
    );
  };

  const {filteredNotifications} = useNotifications()

  const notifications  = filteredNotifications(["deleteTournamentRegistrationOrderItemThunk", "removeDonationOrderItemThunk"])

  const [banner] = useNotificationBanner({
    notifications: notifications,
    timeoutAction: () => {
      dispatch(clearNotifications());
    },
  });

  const getBanner = () => {
    return banner;
  };
  return (
    <event-cart style={{ ...style }}>
      <GSListPage
        title=""
        getPageActions={getPageActions}
        getSearchActions={getSearchActions}
        getItemList={getItemList}
        isSidePanelOpen={isSidePanelOpen}
        getRouter={getRouter}
        headerStyle={headerStyle}
        loading={isLoading}
        getBanner={notifications.length > 0 ? getBanner : false}
      ></GSListPage>
    </event-cart>
  );
};

export default EventCart;
